import { FC, useCallback, useEffect } from 'react';

import { RegisterOptions, useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import './line-items.css';

import { LineItem } from './line-item';
import { LineItemFooter } from './line-item-footer';
import { WidgetInputs } from '../../@types/types';
import { useErrorMessages } from '../../hooks/useErrorMessage';
import { OptionType } from '../select/Select';

export type LineItemsFieldsProps = {
	options: OptionType[];
	currencySymbol: string;
	fieldOptions: {
		Amount?: Partial<Record<keyof WidgetInputs['Amount'], RegisterOptions<Pick<WidgetInputs, 'Amount'>>>>;
		FundKeyOrName?: Partial<
			Record<keyof WidgetInputs['FundKeyOrName'], RegisterOptions<Pick<WidgetInputs, 'FundKeyOrName'>>>
		>;
	};
	defaultFundKey: string | null;
};

export type LineItem = {
	Amount: string;
	FundKeyOrName: string;
};

const MAX_NUMBER_OF_LINE_ITEMS = 5;
const PLACEHOLDER_AMOUNT = '';
const DEFAULT_MAIN_AMOUNT = '0.00';
const ARRAY_FIELD_IDENTIFIER = 'MultiFunds';

export const LineItems: FC<LineItemsFieldsProps> = (props: LineItemsFieldsProps) => {
	const { options, currencySymbol, fieldOptions, defaultFundKey } = props;

	const {
		control,
		register,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext();
	const { fields, append, remove, update } = useFieldArray({
		control,
		name: ARRAY_FIELD_IDENTIFIER,
	});

	const [currentAmount, currentFundKey] = watch(['Amount', 'FundKeyOrName']);
	const { totalAmount, currentFieldsSelection } = useWatch({
		name: ARRAY_FIELD_IDENTIFIER,
		control,
		compute: (data: LineItem[] = []) => ({
			totalAmount: data.reduce((sum, item) => sum + (parseFloat(item.Amount) || 0), 0).toFixed(2),
			currentFieldsSelection: data,
		}),
	});

	const {
		dynamicFields: { MultiFunds: formErrors },
	} = useErrorMessages(fieldOptions, errors);

	useEffect(
		function updateMainAmountField() {
			if (currentFieldsSelection.length) {
				setValue('Amount', totalAmount, { shouldValidate: totalAmount !== DEFAULT_MAIN_AMOUNT });
			}
		},
		[currentFieldsSelection.length, setValue, totalAmount]
	);

	const getNextKey = useCallback(() => {
		const selectedFundsByOthers: string[] =
			currentFieldsSelection?.map((item: LineItem) => item.FundKeyOrName) || [];

		const fundsToExclude = fields.length <= 1 ? [currentFundKey] : selectedFundsByOthers;
		let firstNonDisabledKey: string;

		if (defaultFundKey) {
			if (!fundsToExclude.includes(defaultFundKey)) {
				firstNonDisabledKey = defaultFundKey;
			} else {
				firstNonDisabledKey =
					options.find(option => !fundsToExclude.includes(option.value) && !option.disabled)?.value ||
					options[0]?.value ||
					'';
			}
		} else {
			firstNonDisabledKey = '';
		}

		return firstNonDisabledKey;
	}, [currentFieldsSelection, currentFundKey, defaultFundKey, fields.length, options]);

	const onClickAddLineItem = useCallback(() => {
		if (fields.length === 0) {
			let amount = PLACEHOLDER_AMOUNT;
			if (currentAmount !== '') {
				if (!Number.isNaN(parseFloat(currentAmount))) {
					amount = parseFloat(currentAmount).toFixed(2);
				}
			}

			append({
				Amount: amount,
				FundKeyOrName: currentFundKey,
			});
		} else if (fields.length === 1) {
			update(0, { Amount: currentAmount, FundKeyOrName: currentFundKey });
		}

		append({ Amount: PLACEHOLDER_AMOUNT, FundKeyOrName: getNextKey() });
	}, [append, currentAmount, currentFundKey, fields.length, getNextKey, update]);

	const onClickRemoveLineItem = useCallback(
		(index: number) => {
			if (currentFieldsSelection.length === 2) {
				const remainingIndex = index === 0 ? 1 : 0;
				setValue('Amount', currentFieldsSelection[remainingIndex].Amount);
				setValue('FundKeyOrName', currentFieldsSelection[remainingIndex].FundKeyOrName);
				setValue('MultiFunds', []);
				return;
			}
			remove(index);
		},
		[currentFieldsSelection, remove, setValue]
	);

	const optionsWithDisabledState = useCallback(
		(index: number) => {
			const selectedFundsByOthers: string[] =
				currentFieldsSelection
					?.map((item: LineItem) => item.FundKeyOrName)
					.filter((_: string, i: number) => i !== index) || [];

			return options.map((option: OptionType) => ({
				...option,
				disabled: option.value !== '' && (selectedFundsByOthers.includes(option.value) || option.disabled),
			}));
		},
		[currentFieldsSelection, options]
	);

	const hasReachedMaxLineItemsDueToOptions = defaultFundKey
		? options.length === fields.length
		: options.length - 1 === fields.length;

	const hasReachedMaxLineItems =
		fields.length >= MAX_NUMBER_OF_LINE_ITEMS || options.length === 1 || hasReachedMaxLineItemsDueToOptions;

	return (
		<div>
			{fields.map((field, index: number) => (
				<LineItem
					key={field.id}
					currencySymbol={currencySymbol}
					errorMessages={formErrors?.[index] || {}}
					fields={{
						amount: {
							...register(`MultiFunds.${index}.Amount`, fieldOptions.Amount),
							acceptanceTestTargetId: `multi-fund-${index}-amount-input`,
						},
						select: {
							...register(`MultiFunds.${index}.FundKeyOrName`, fieldOptions.FundKeyOrName),
							acceptanceTestTargetId: `multi-fund-${index}-select-input`,
						},
					}}
					itemKey={field.id}
					options={optionsWithDisabledState(index)}
					onClickRemove={() => onClickRemoveLineItem(index)}
				/>
			))}
			<LineItemFooter
				currencySymbol={currencySymbol}
				hasFields={fields.length > 0}
				totalAmount={totalAmount}
				onClickAddLineItem={hasReachedMaxLineItems ? undefined : onClickAddLineItem}
			/>
		</div>
	);
};
